import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import FiIcons from '../fiIconMap';
import LayoutDefault from '../layouts/LayoutDefault';
import RouteSeo from '../../../seo/RouteSeo';
import { MARKETING_PAGE_META } from '../../../data/marketingPageMeta';
import '../assets/scss/style.scss';

const PERSONA_CARDS = [
    {
        path: '/for/mental-health',
        title: 'Just texting yourself',
        description:
            'Sometimes getting it out of your head is the whole point. A private place for the 3am thoughts.'
    },
    {
        path: '/for/people-who-text-themselves',
        title: 'People who text themselves',
        description:
            'You already send yourself links, screenshots, and reminders. Keep the habit. Luckynote makes every message findable again.'
    },
    {
        path: '/for/adhd',
        title: 'ADHD brains',
        description:
            'No structure to maintain, no system to fail at. One inbox first, zero-decision capture, and Luckynote doing the remembering.'
    },
    {
        path: '/for/indie-hackers',
        title: 'Indie hackers',
        description:
            'Building alone means holding everything alone. Save research, screenshots, and voice notes in one searchable inbox so context does not disappear.'
    },
    {
        path: '/for/founders',
        title: 'Founders',
        description:
            'Keep customer language, competitor changes, product decisions, and the ideas behind them together so you can move without losing the thread.'
    },
    {
        path: '/for/solopreneurs',
        title: 'Solopreneurs',
        description:
            'When every part of the business is yours, capture the useful bits fast and find them again without building another system to maintain.'
    },
    {
        path: '/for/creators',
        title: 'Creators',
        description:
            'Save Instagram and TikTok inspiration, hooks, references, voice-note ideas, and brand research in one searchable place.'
    },
    {
        path: '/for/tab-hoarders',
        title: 'Tab hoarders',
        description:
            'Save the page, close the tab, and trust you can find it later without rebuilding the whole trail from bookmarks or browser history.'
    },
    {
        path: '/for/students',
        title: 'Students',
        description:
            'Keep lecture slides, whiteboard photos, PDFs, links, and voice memos in one searchable place before exams.'
    },
    {
        path: '/for/writers',
        title: 'Writers',
        description:
            'Save article clips, book-page photos, voice-note ideas, and source material in one searchable inbox so the trail back to an idea stays intact.'
    },
    {
        path: '/for/researchers',
        title: 'Researchers',
        description:
            'Keep papers, figure screenshots, talk notes, files, and source links together so important material is easier to recover months later.'
    },
    {
        path: '/for/parents',
        title: 'Parents',
        description:
            'Hold onto school papers, class-group screenshots, appointment notes, reminders, and family logistics without carrying them all in your head.'
    },
    {
        path: '/for/shopping',
        title: 'Shopping',
        description:
            'Save product links, screenshots, receipts, sizes, and reminders in one inbox so your research survives beyond one session.'
    }
];

// Newest lastmod first. Missing lastmod always sorts last. Equal dates keep
// their existing relative order (Array.prototype.sort is stable), preserving
// the curated PERSONA_CARDS ordering as a tie-break.
const byLastmodDesc = (a, b) => {
    if (!a.lastmod && !b.lastmod) return 0;
    if (!a.lastmod) return 1;
    if (!b.lastmod) return -1;
    if (a.lastmod > b.lastmod) return -1;
    if (a.lastmod < b.lastmod) return 1;
    return 0;
};

const ForPage = () => {
    const personas = PERSONA_CARDS.map((persona) => {
        const page = MARKETING_PAGE_META.find((candidate) => candidate.path === persona.path);
        return {
            ...persona,
            icon: page?.icon,
            color: page?.iconColor,
            background: page?.iconBackground,
            lastmod: page?.lastmod
        };
    }).filter((persona) => persona.icon)
        .sort(byLastmodDesc);

    return (
        <>
            <RouteSeo
                title="Who Luckynote Is For | Luckynote"
                description="Luckynote is one chat where you send yourself everything, then makes it findable. See how it fits founders, solopreneurs, creators, ADHD brains, and more."
                canonical="https://luckynote.io/for"
            />
            <LayoutDefault>
                <DarkSection>
                    <PageShell>
                        <HeroSection>
                            <HeroTitle>Built for how you already work</HeroTitle>
                            <HeroDescription>
                                Luckynote is not a system to adopt. It is the capture habit you already
                                have. Send it to one place, with Luckynote search underneath. Find your people below.
                            </HeroDescription>
                        </HeroSection>

                        <CardGrid>
                            {personas.map((persona) => {
                                const IconComponent = FiIcons[persona.icon];
                                return (
                                    <PersonaCard key={persona.path} to={persona.path}>
                                        <IconBadge $color={persona.color} $background={persona.background}>
                                            <IconComponent size={22} />
                                        </IconBadge>
                                        <CardTitle>{persona.title}</CardTitle>
                                        <CardBody>{persona.description}</CardBody>
                                        <CardCta>
                                            This is me
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </CardCta>
                                    </PersonaCard>
                                );
                            })}
                        </CardGrid>

                        <FooterNote>
                            Deciding between apps instead? <Link to="/compare">See how Luckynote compares</Link>.
                            Or browse workflows on the <Link to="/use-cases">use cases</Link> page.
                        </FooterNote>
                    </PageShell>
                </DarkSection>
            </LayoutDefault>
        </>
    );
};

export default ForPage;

const PAPER = '#fcf9f7';
const PAPER_DEEP = '#f6efe7';

const DarkSection = styled.div`
    background:
        radial-gradient(circle at 14% 4%, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0) 26rem),
        linear-gradient(180deg, ${PAPER} 0%, ${PAPER_DEEP} 56%, ${PAPER} 100%);
    min-height: 100vh;
`;

const PageShell = styled.div`
    max-width: 1120px;
    margin: 0 auto;
    padding: 96px 20px 96px;
`;

const HeroSection = styled.section`
    padding: 0 0 56px;
    text-align: center;
`;

const HeroTitle = styled.h1`
    margin: 0 0 16px;
    font-size: clamp(2rem, 4vw, 3.2rem);
    line-height: 1.1;
    letter-spacing: -0.02em;
    font-weight: 700;
    color: #14110f;
`;

const HeroDescription = styled.p`
    margin: 0 auto;
    font-size: 1.1rem;
    line-height: 1.7;
    color: rgba(20, 17, 15, 0.66);
    max-width: 38rem;
`;

const CardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 20px;
`;

const PersonaCard = styled(Link)`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 28px 26px;
    border-radius: 14px;
    border: 1px solid rgba(20, 17, 15, 0.12);
    background: rgba(255, 255, 255, 0.58);
    text-decoration: none;
    backdrop-filter: blur(12px);
    transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.88);
        border-color: rgba(20, 17, 15, 0.22);
        box-shadow: 0 18px 42px rgba(59, 46, 35, 0.16);
        transform: translateY(-3px);
    }
`;

const IconBadge = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ $background }) => $background};
    color: ${({ $color }) => $color};
`;

const CardTitle = styled.h3`
    margin: 0;
    font-size: 1.15rem;
    color: #14110f;
`;

const CardBody = styled.p`
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.65;
    color: rgba(20, 17, 15, 0.62);
    flex-grow: 1;
`;

const CardCta = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.9rem;
    font-weight: 600;
    color: #2b88d9;

    svg {
        transition: transform 0.15s ease;
    }

    ${PersonaCard}:hover & svg {
        transform: translateX(3px);
    }
`;

const FooterNote = styled.p`
    margin: 48px auto 0;
    text-align: center;
    font-size: 0.95rem;
    color: rgba(20, 17, 15, 0.62);

    a {
        color: #2b88d9;
        font-weight: 600;
    }
`;
